import React, { useState, useContext, useEffect } from 'react';
import { Paper } from '@material-ui/core';
import { UserContext } from '../../../contexts/UserContext';
import { FilterAndGroupByContainer } from './inventoryHistoryV2/FilterAndGroupByContainer';
import { InventoryHistoryTableContainer } from './inventoryHistoryV2/InventoryHistoryTableContainer';
import { Notifications } from '../../../shared/components/Notifications';
import { CopyToClipboard } from '../../../shared/components/copyToClipboard';
import { formatLocalTimeString } from '../../../utils/dateUtils/ledgerDateUtils';

const InventoryHistoryV2 = () => {
    const [notification, setNotification] = useState({ text: null, level: null });
    const user = useContext(UserContext);
    const [activeFilters, setActiveFilters] = useState([]);
    const [groupBy, setGroupBy] = useState('sku');
    const [summarizePackout, setSummarizePackout] = useState(true);
    const [initialFilters, setInitialFilters] = useState(null);

    // Update URL with filter parameters for sharing
    const updateUrlWithFilters = (filters, currentGroupBy, currentSummarizePackout) => {
        const searchParams = new URLSearchParams();
        
        // Add groupBy and summarizePackout parameters
        searchParams.set('groupBy', currentGroupBy);
        searchParams.set('summarizePackout', currentSummarizePackout.toString());
        
        // Process filters
        if (filters && filters.length > 0) {
            filters.forEach(filter => {
                if (!filter || !filter.filterKey) return;
                
                const { filterKey, data } = filter;
                
                // Handle dateRange special case
                if (filterKey === 'dateRange' && data?.startDate && data?.endDate) {
                    const startDate = new Date(data.startDate);
                    const endDate = new Date(data.endDate);
                    searchParams.set('startDate', startDate.toISOString());
                    searchParams.set('endDate', endDate.toISOString());
                    return;
                }
                
                // Handle all other filters
                if (data !== undefined && data !== null) {
                    if (Array.isArray(data)) {
                        // Handle array data (like multiCheck filters)
                        data.forEach(item => {
                            searchParams.append(filterKey, item);
                        });
                    } else {
                        // Handle single value data (like textInput filters)
                        searchParams.append(filterKey, data);
                    }
                }
            });
        }
        
        const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
        window.history.pushState({ path: newUrl }, '', newUrl);
    };

    // Parse URL parameters on component mount
    const parseUrlParams = () => {
        const searchParams = new URLSearchParams(window.location.search);
        const hasUrlParams = searchParams.toString().length > 0;
        
        if (hasUrlParams) {
            const groupByParam = searchParams.get('groupBy');
            if (groupByParam && ['sku', 'lot'].includes(groupByParam)) {
                setGroupBy(groupByParam);
            }
            
            const summarizeParam = searchParams.get('summarizePackout');
            if (summarizeParam !== null) {
                setSummarizePackout(summarizeParam === 'true');
            }
            
            const startDate = searchParams.get('startDate');
            const endDate = searchParams.get('endDate');
            
            if (startDate && endDate) {
                const urlFilters = [];
                
                const startObj = new Date(startDate);
                const endObj = new Date(endDate);
                
                urlFilters.push({
                    type: 'dateRange',
                    data: {
                        startDate: startObj,
                        endDate: endObj
                    },
                    chipLabel: `Date Range: ${formatLocalTimeString(startObj)} - ${formatLocalTimeString(endObj)}`,
                    filterKey: 'dateRange',
                    deletable: false
                });
                
                const eventTypes = searchParams.getAll('eventType');
                eventTypes.forEach(eventType => {
                    urlFilters.push({
                        type: 'multiCheck',
                        data: [eventType],
                        chipLabel: `Event Type: ${eventType}`,
                        filterKey: 'eventType',
                        deletable: true
                    });
                });
                
                const skus = searchParams.getAll('sku');
                skus.forEach(sku => {
                    urlFilters.push({
                        type: 'textInput',
                        data: sku,
                        chipLabel: `SKU: ${sku}`,
                        filterKey: 'sku',
                        deletable: true
                    });
                });
                
                const lots = searchParams.getAll('lot');
                lots.forEach(lot => {
                    urlFilters.push({
                        type: 'textInput',
                        data: lot,
                        chipLabel: `Lot: ${lot}`,
                        filterKey: 'lot',
                        deletable: true
                    });
                });
                
                const itemNames = searchParams.getAll('itemName');
                itemNames.forEach(itemName => {
                    urlFilters.push({
                        type: 'textInput',
                        data: itemName,
                        chipLabel: `Item Name: ${itemName}`,
                        filterKey: 'itemName',
                        deletable: true
                    });
                });
                
                const partnerSkus = searchParams.getAll('partnerSku');
                partnerSkus.forEach(partnerSku => {
                    urlFilters.push({
                        type: 'textInput',
                        data: partnerSku,
                        chipLabel: `Partner SKU: ${partnerSku}`,
                        filterKey: 'partnerSku',
                        deletable: true
                    });
                });
                
                const locations = searchParams.getAll('location');
                locations.forEach(location => {
                    urlFilters.push({
                        type: 'textInput',
                        data: location,
                        chipLabel: `Location: ${location}`,
                        filterKey: 'location',
                        deletable: true
                    });
                });
                
                const sources = searchParams.getAll('source');
                sources.forEach(source => {
                    urlFilters.push({
                        type: 'select',
                        data: source,
                        chipLabel: `Source: ${source}`,
                        filterKey: 'source',
                        deletable: true
                    });
                });
                
                const reasons = searchParams.getAll('reason');
                reasons.forEach(reason => {
                    urlFilters.push({
                        type: 'select',
                        data: reason,
                        chipLabel: `Reason Code: ${reason}`,
                        filterKey: 'reason',
                        deletable: true
                    });
                });
                
                const users = searchParams.getAll('user');
                users.forEach(user => {
                    urlFilters.push({
                        type: 'textInput',
                        data: user,
                        chipLabel: `User: ${user}`,
                        filterKey: 'user',
                        deletable: true
                    });
                });
                
                const palletIds = searchParams.getAll('palletId');
                palletIds.forEach(palletId => {
                    urlFilters.push({
                        type: 'textInput',
                        data: palletId,
                        chipLabel: `Pallet ID: ${palletId}`,
                        filterKey: 'palletId',
                        deletable: true
                    });
                });
                
                if (urlFilters.length > 0) {
                    setInitialFilters(urlFilters);
                }
            }
        }
    };

    // Parse URL parameters on component mount
    useEffect(() => {
        parseUrlParams();
    }, []);

    const handleFiltersChange = (newFilters) => {
        // Check if dateRange filter exists and has both startDate and endDate
        const dateRangeFilter = newFilters.find(filter => filter.filterKey === 'dateRange');
        const hasValidDateRange = dateRangeFilter && 
            dateRangeFilter.data && 
            dateRangeFilter.data.startDate && 
            dateRangeFilter.data.endDate;
            
        if (hasValidDateRange) {
            setActiveFilters(newFilters);
            updateUrlWithFilters(newFilters, groupBy, summarizePackout);
        } else {
            // Optional: Add notification that date range is required
            setNotification({
                text: 'A valid date range with both start and end dates is required',
                level: 'error'
            });
        }
    };

    const handleGroupChange = (newGroupBy) => {
        setGroupBy(newGroupBy);
        updateUrlWithFilters(activeFilters, newGroupBy, summarizePackout);
    };

    const handleSummarizePackoutChange = (newValue) => {
        setSummarizePackout(newValue);
        updateUrlWithFilters(activeFilters, groupBy, newValue);
    };

    return (
        <Paper style={{ minHeight: '80vh', padding: '20px' }}>
            <Notifications options={notification} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h1 style={{ textAlign: 'left', margin: 0 }}>Inventory Event Log</h1>
                <CopyToClipboard value={window.location.href} />
            </div>
            <FilterAndGroupByContainer
                facilityId={user.getFacilityID()}
                onFiltersChange={handleFiltersChange}
                onGroupChange={handleGroupChange}
                setNotification={setNotification}
                onSummarizePackoutChange={handleSummarizePackoutChange}
                initialFilters={initialFilters}
                initialGroupBy={groupBy}
                initialSummarizePackout={summarizePackout}
            />
            <InventoryHistoryTableContainer
                facilityId={user.getFacilityID()}
                groupBy={groupBy}
                activeFilters={activeFilters}
                setNotification={setNotification}
                summarizePackout={summarizePackout}
            />
        </Paper>
    );
};

export { InventoryHistoryV2 };
